/**
 * Spatial Clustering Service
 *
 * Implements server-side spatial clustering using PostGIS for stable,
 * accurate recall aggregation without client-side sampling limitations.
 */

import { getProdSupabaseClient } from '@/lib/supabase/client';
import {
  getTenantIdFromSession,
  getRecallsForTenantWithFilters
} from '@/lib/supabase/tenantScoping';
import { getBrandsForOEM, DEFAULT_OEM } from '@/constants/oems';
import { filterSafetyRecallsForMapDisplayByOem } from '@/lib/utils/safetyRecallCampaignFilter';

export interface ViewportBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface GetRecallClustersOptions {
  snapshotDate?: string | null; // Optional snapshot date filter (YYYY-MM-DD format)
  /** Required: recall counts are always for this OEM only (e.g. Ford, Stellantis). */
  oem: string;
}

export interface RecallCluster {
  clusterId: string;
  centerLat: number;
  centerLon: number;
  recallCount: number;
  vehicleCount: number;
  dealerCount: number;
  dealerNames: string[];
  makes: string[]; // Unique vehicle makes at this dealer for OEM filtering
  sampleDealers: Array<{
    name: string;
    lat: number;
    lon: number;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
  }>;
}

/**
 * Process fallback data when safety_recalls JSON is corrupted
 * Just counts VINs per dealer without recall information
 */
function processFallbackData(data: any[]): RecallCluster[] {
  const dealerMap = new Map<
    string,
    {
      name: string;
      lat: number;
      lon: number;
      address: string;
      city: string;
      state: string;
      zip: string;
      vins: Set<string>;
      makes: Set<string>;
    }
  >();

  data.forEach(row => {
    const dealerKey = `${row.service_provider_name}_${row.seller_latitude}_${row.seller_longitude}`;

    if (!dealerMap.has(dealerKey)) {
      dealerMap.set(dealerKey, {
        name: row.service_provider_name || 'Unknown',
        lat: row.seller_latitude,
        lon: row.seller_longitude,
        address: row.seller_street || 'N/A',
        city: row.seller_city || 'N/A',
        state: row.seller_region || 'N/A',
        zip: row.seller_postal_code || 'N/A',
        vins: new Set(),
        makes: new Set()
      });
    }

    const dealer = dealerMap.get(dealerKey)!;
    dealer.vins.add(row.vin);
    if (row.make_name) {
      dealer.makes.add(row.make_name);
    }
  });

  return Array.from(dealerMap.entries()).map(([key, dealer]) => ({
    clusterId: key,
    centerLat: dealer.lat,
    centerLon: dealer.lon,
    recallCount: dealer.vins.size, // Estimate: 1 recall per VIN (fallback when data is corrupted)
    vehicleCount: dealer.vins.size,
    dealerCount: 1,
    dealerNames: [dealer.name],
    makes: Array.from(dealer.makes),
    sampleDealers: [
      {
        name: dealer.name,
        lat: dealer.lat,
        lon: dealer.lon,
        address: dealer.address,
        city: dealer.city,
        state: dealer.state,
        zip: dealer.zip
      }
    ]
  }));
}

export interface DealerCluster {
  dealerKey: string;
  dealerName: string;
  dealerLat: number;
  dealerLon: number;
  recallCount: number;
  vehicleCount: number;
  campaignCount: number;
  dealerInfo: {
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    phone?: string;
  };
}

/**
 * Get hex-binned clusters for map viewport
 * Uses server-side spatial aggregation for stable totals at any zoom level
 */
export async function getRecallClusters(
  bounds: ViewportBounds,
  zoomLevel: number = 10,
  userId?: string,
  options?: GetRecallClustersOptions
): Promise<RecallCluster[]> {
  try {
    // Use prod schema client to access prod.cars_with_safety_recalls_mv
    const prodClient = getProdSupabaseClient();

    // Apply dealer exclusions if userId provided
    const excludedDealers = userId
      ? await import('@/features/recalls/services/queryBuilder').then(({ getExcludedDealers }) =>
          getExcludedDealers(prodClient, userId)
        )
      : [];

    // Use RPC function to get tenant-scoped recalls with viewport bounds
    // For Control Center (super_admin), we need to show all recalls regardless of tenant zones
    // This restores the previous behavior before tenancy was implemented
    const tenantId = await getTenantIdFromSession(prodClient);

    // Check if user is super_admin by querying their role
    let isSuperAdmin = false;
    if (userId) {
      try {
        // Query user_roles with join to roles table to check for super_admin
        const { data: roleData } = await prodClient
          .from('user_roles')
          .select('role_id, roles(id, name)')
          .eq('user_id', userId)
          .eq('is_active', true)
          .limit(10); // Get all active roles to check for super_admin

        // Check if any role has name 'super_admin'
        isSuperAdmin = (roleData || []).some((ur: any) => ur.roles?.name === 'super_admin');
      } catch (err) {
        console.warn('[spatialClustering] Failed to check super_admin role:', err);
      }
    }

    // Check if tenant type is 'internal' (skip zip code filtering)
    let isInternalTenant = false;
    if (tenantId) {
      try {
        const { data: tenantData } = await prodClient
          .from('tenants')
          .select('tenant_type')
          .eq('id', tenantId)
          .maybeSingle();

        isInternalTenant = tenantData?.tenant_type === 'internal';
      } catch (err) {
        console.warn('[spatialClustering] Failed to check tenant type:', err);
      }
    }

    let data: any[] = [];
    let error: any = null;

    // Convert snapshot date string to YYYY-MM-DD format if provided
    const snapshotDateParam = options?.snapshotDate
      ? options.snapshotDate.includes('T')
        ? options.snapshotDate.split('T')[0]
        : options.snapshotDate
      : null;

    if (tenantId) {
      // For internal tenants, use RPC function that filters to latest snapshot_date per zone_slug
      if (isInternalTenant) {
        try {
          // Use the new RPC function that automatically filters to latest snapshot_date per zone_slug
          // This prevents double counting of the same recalls across multiple snapshot dates
          const abortId = `${snapshotDateParam || 'all'}:${bounds.south}:${bounds.north}:${bounds.west}:${bounds.east}`;
          let allRpcData: any[] = [];
          const PAGE_SIZE = 10000;
          let offset = 0;
          let hasMore = true;
          let pageCount = 0;
          const MAX_PAGES = 50; // Safety limit: 500k rows max
          let rpcError: any = null;

          while (hasMore && pageCount < MAX_PAGES) {
            // Use get_recalls_for_viewport_latest_per_zone for internal tenants
            // This automatically filters to latest snapshot_date per zone_slug
            // Use viewport bounds for performance (same as dealer tenants)
            const result = await prodClient.rpc('get_recalls_for_viewport_latest_per_zone', {
              p_south_lat: bounds.south,
              p_north_lat: bounds.north,
              p_west_lng: bounds.west,
              p_east_lng: bounds.east,
              p_snapshot_date: null, // NULL = compute latest date per zone_slug automatically
              p_limit: PAGE_SIZE,
              p_offset: offset
            });

            rpcError = result.error;
            if (rpcError) {
              // If the new RPC doesn't exist, fall back to the old one
              if (rpcError.message?.includes('function') || rpcError.code === '42883') {
                console.warn(
                  '[spatialClustering] get_recalls_for_viewport_latest_per_zone not found, falling back to get_recalls_for_viewport'
                );
                // Fall back to old RPC and filter client-side
                const fallbackResult = await prodClient.rpc('get_recalls_for_viewport', {
                  p_south_lat: bounds.south,
                  p_north_lat: bounds.north,
                  p_west_lng: bounds.west,
                  p_east_lng: bounds.east,
                  p_snapshot_date: snapshotDateParam,
                  p_limit: PAGE_SIZE,
                  p_offset: offset
                });
                rpcError = fallbackResult.error;
                if (rpcError) {
                  error = rpcError;
                  hasMore = false;
                  break;
                }
                const pageData = fallbackResult.data || [];
                allRpcData = [...allRpcData, ...pageData];
              } else {
                error = rpcError;
                hasMore = false;
                break;
              }
            } else {
              const pageData = result.data || [];
              allRpcData = [...allRpcData, ...pageData];
            }

            // Check if we got a full page (means there might be more)
            if ((rpcError ? 0 : (result.data || []).length) < PAGE_SIZE) {
              hasMore = false;
            } else {
              offset += PAGE_SIZE;
              pageCount++;
            }
          }

          // Tier 1 Fix: De-duplicate by unique (vin, campaign_number) pairs to avoid double-counting across pages
          // Track unique pairs in a Set, then rebuild rows grouped by VIN with deduplicated safety_recalls arrays
          const uniquePairs = new Set<string>();
          const vinRowMap = new Map<string, any>();

          for (const row of allRpcData) {
            const vin = row.vin;
            if (!vin) continue;

            // Initialize VIN entry if not seen
            if (!vinRowMap.has(vin)) {
              vinRowMap.set(vin, {
                ...row,
                safety_recalls: []
              });
            }

            const vinRow = vinRowMap.get(vin);
            const safetyRecalls = row.safety_recalls;

            // Expand safety_recalls JSONB array and deduplicate by vin::campaign_number
            if (safetyRecalls && Array.isArray(safetyRecalls)) {
              for (const recall of safetyRecalls) {
                const campaignNumber = recall?.campaign_number;
                if (!campaignNumber) continue;

                const pairKey = `${vin}::${campaignNumber}`;

                // Only add this (vin, campaign_number) pair if we haven't seen it before
                if (!uniquePairs.has(pairKey)) {
                  uniquePairs.add(pairKey);
                  vinRow.safety_recalls.push(recall);
                }
              }
            }
          }

          // Convert map back to array and filter out rows with no recalls
          let dedupedData = Array.from(vinRowMap.values()).filter(
            row => row.safety_recalls && row.safety_recalls.length > 0
          );

          // If we fell back to the old RPC, apply client-side filtering for latest snapshot per zone_slug
          // (The new RPC already does this server-side)
          if (rpcError && rpcError.message?.includes('function')) {
            console.log(
              '[spatialClustering] Applying client-side filter for latest snapshot per zone_slug (fallback)'
            );
            const { filterRecallsByLatestSnapshotPerZoneSlug } = await import(
              '@/features/admin/utils/filterRecallsByLatestSnapshot'
            );
            dedupedData = await filterRecallsByLatestSnapshotPerZoneSlug(dedupedData);
          }

          // Log deduplication stats
          const totalRowsBefore = allRpcData.length;
          const totalRowsAfter = dedupedData.length;
          const totalUniquePairs = uniquePairs.size;

          if (totalRowsBefore !== totalRowsAfter) {
            console.warn(
              `[spatialClustering] Deduplicated ${totalRowsBefore} rows to ${totalRowsAfter} unique VIN rows with ${totalUniquePairs} unique (vin, campaign_number) pairs`
            );
          }

          data = dedupedData;

          if (pageCount > 0) {
            console.log(
              `[spatialClustering] Fetched ${allRpcData.length} total rows (${dedupedData.length} unique VINs after dedupe) across ${pageCount + 1} page(s) for internal tenant`
            );
          }

          if (rpcError) {
            console.warn(
              '[spatialClustering] get_recalls_for_viewport RPC error (internal tenant):',
              rpcError
            );
            if (rpcError.message?.includes('function') || rpcError.code === '42883') {
              console.warn(
                '[spatialClustering] get_recalls_for_viewport RPC function not found - migration may not be applied'
              );
            }
          }
        } catch (err: any) {
          error = err;
          console.error('[spatialClustering] RPC query exception (internal tenant):', err);
        }
      } else {
        // For super admins, skip tenant-scoped query and go directly to get_recalls_for_viewport
        // This avoids the 404 error from get_recalls_for_tenant_with_filters which doesn't exist
        if (isSuperAdmin) {
          console.log(
            '[spatialClustering] Super admin detected - using get_recalls_for_viewport directly'
          );
          // Skip tenant-scoped query and go directly to fallback
          data = [];
          error = null; // Set error to null so we proceed to fallback
        } else {
          // Regular tenant - use tenant-scoped query with zip code filtering
          let result;
          try {
            result = await getRecallsForTenantWithFilters(prodClient, tenantId, {
              southLat: bounds.south,
              northLat: bounds.north,
              westLng: bounds.west,
              eastLng: bounds.east,
              limit: 10000,
              offset: 0
            });

            // The RPC function now filters to latest snapshot_date per zone_slug server-side
            data = result.data || [];
            error = result.error;

            // If RPC function doesn't exist, this is a real error for regular tenants
            if (error?.code === 'RPC_NOT_FOUND') {
              console.warn(
                '[spatialClustering] get_recalls_for_tenant_with_filters RPC function not found in database. This function should exist for tenant-scoped queries.'
              );
            }
          } catch (err: any) {
            // If the function doesn't exist or fails, treat as error
            if (
              err?.code === '404' ||
              err?.message?.includes('404') ||
              err?.message?.includes('Not Found')
            ) {
              console.warn(
                '[spatialClustering] get_recalls_for_tenant_with_filters RPC function not found (404). This function should exist for tenant-scoped queries.'
              );
            } else {
              console.warn('[spatialClustering] getRecallsForTenantWithFilters failed:', err);
            }
            error = err;
            data = [];
          }
        }

        // Only fall back to querying all recalls if:
        // 1. User is super_admin (should see all recalls)
        // 2. OR tenant-scoped query returns an actual error (not just empty data)
        // For regular tenants with no zones, return empty data (don't show all recalls)
        if (isSuperAdmin && (error || !data || data.length === 0)) {
          console.log('[spatialClustering] Super admin detected - falling back to all recalls');

          // Query using RPC function that bypasses tenant zone filtering
          // Direct SELECT on materialized view is revoked, so we must use RPC functions
          // Use get_recalls_for_viewport which doesn't filter by tenant zones (for Control Center)
          let allData: any[] = [];
          let allError: any = null;

          try {
            console.log('[spatialClustering] Calling get_recalls_for_viewport with:', {
              bounds,
              snapshotDate: snapshotDateParam,
              hasSnapshotDate: !!snapshotDateParam
            });

            // Fetch all data using pagination if needed
            let paginatedData: any[] = [];
            const PAGE_SIZE = 10000;
            let offset = 0;
            let hasMore = true;
            let pageCount = 0;
            const MAX_PAGES = 50; // Safety limit: 500k rows max

            let rpcError: any = null;
            while (hasMore && pageCount < MAX_PAGES) {
              const result = await prodClient.rpc('get_recalls_for_viewport', {
                p_south_lat: bounds.south,
                p_north_lat: bounds.north,
                p_west_lng: bounds.west,
                p_east_lng: bounds.east,
                p_snapshot_date: snapshotDateParam,
                p_limit: PAGE_SIZE,
                p_offset: offset
              });

              rpcError = result.error;
              if (rpcError) {
                allError = rpcError;
                hasMore = false;
                if (rpcError.message?.includes('function') || rpcError.code === '42883') {
                  console.warn(
                    '[spatialClustering] get_recalls_for_viewport RPC function not found - migration may not be applied'
                  );
                }
                break;
              }

              const pageData = result.data || [];
              paginatedData = [...paginatedData, ...pageData];

              // Check if we got a full page (means there might be more)
              if (pageData.length < PAGE_SIZE) {
                hasMore = false;
              } else {
                offset += PAGE_SIZE;
                pageCount++;
              }
            }

            // Tier 1 Fix: De-duplicate rows by VIN to avoid duplicate VIN rows across pages
            // The RPC returns rows with safety_recalls as JSONB arrays (one row per VIN)
            // Downstream processing already handles unique (vin, campaign_number) pairs correctly
            const dedupeMap = new Map<string, any>();
            for (const row of paginatedData) {
              const key =
                row.vin ||
                `${row.seller_latitude}_${row.seller_longitude}_${row.service_provider_name}`;
              if (!dedupeMap.has(key)) {
                dedupeMap.set(key, row);
              }
            }

            const dedupedPaginatedData = Array.from(dedupeMap.values());
            if (paginatedData.length !== dedupedPaginatedData.length) {
              console.warn(
                `[spatialClustering] Deduplicated ${paginatedData.length} rows to ${dedupedPaginatedData.length} unique VINs (removed ${paginatedData.length - dedupedPaginatedData.length} duplicate VIN rows)`
              );
            }

            // For super admins (internal tenants), use RPC function that filters to latest snapshot_date per zone_slug
            // This prevents double counting of the same recalls across multiple snapshot dates
            const { data: rpcDataFiltered, error: rpcErrorFiltered } = await prodClient.rpc(
              'get_recalls_for_viewport_latest_per_zone',
              {
                p_south_lat: bounds.south,
                p_north_lat: bounds.north,
                p_west_lng: bounds.west,
                p_east_lng: bounds.east,
                p_snapshot_date: snapshotDateParam,
                p_limit: 50000, // Large limit to get all data
                p_offset: 0
              }
            );

            if (rpcErrorFiltered) {
              console.warn(
                '[spatialClustering] get_recalls_for_viewport_latest_per_zone RPC error, falling back to client-side filter:',
                rpcErrorFiltered
              );
              // Fallback to client-side filtering if RPC fails
              const { filterRecallsByLatestSnapshotPerZoneSlug } = await import(
                '@/features/admin/utils/filterRecallsByLatestSnapshot'
              );
              allData = await filterRecallsByLatestSnapshotPerZoneSlug(dedupedPaginatedData);
            } else {
              // Deduplicate RPC results by VIN
              const dedupeMap = new Map<string, any>();
              for (const row of rpcDataFiltered || []) {
                const key =
                  row.vin ||
                  `${row.seller_latitude}_${row.seller_longitude}_${row.service_provider_name}`;
                if (!dedupeMap.has(key)) {
                  dedupeMap.set(key, row);
                }
              }
              allData = Array.from(dedupeMap.values());
            }

            if (pageCount > 0) {
              console.log(
                `[spatialClustering] get_recalls_for_viewport returned ${paginatedData.length} total rows (${dedupedPaginatedData.length} after dedupe, ${allData.length} after latest snapshot filter) across ${pageCount + 1} page(s)`
              );
            } else {
              console.log(
                `[spatialClustering] get_recalls_for_viewport returned ${paginatedData.length} recalls (${allData.length} after latest snapshot filter)`
              );
            }

            if (snapshotDateParam && (!paginatedData || paginatedData.length === 0)) {
              console.warn(
                `[spatialClustering] No recalls found for snapshot date ${snapshotDateParam} in viewport bounds. Records for this date may be in a different geographic area.`
              );
            }

            if (allError) {
              console.warn('[spatialClustering] get_recalls_for_viewport RPC error:', allError);
            }
          } catch (err: any) {
            allError = err;
            console.error('[spatialClustering] RPC query exception:', err);
          }

          if (!allError && allData) {
            data = allData;
            error = null;
            console.log(`[spatialClustering] Fallback query returned ${data.length} recalls`);
          } else if (allError) {
            console.warn('[spatialClustering] Fallback query error:', allError);
            console.error(
              '[spatialClustering] Full error details:',
              JSON.stringify(allError, null, 2)
            );
          }
        }
      }
    } else {
      // No tenant ID - query all recalls (for super_admin or Control Center)
      console.log(
        '[spatialClustering] No tenant ID, querying all recalls using get_recalls_for_viewport'
      );

      // Use RPC function that bypasses tenant zone filtering
      const { data: rpcData, error: rpcError } = await prodClient.rpc('get_recalls_for_viewport', {
        p_south_lat: bounds.south,
        p_north_lat: bounds.north,
        p_west_lng: bounds.west,
        p_east_lng: bounds.east,
        p_snapshot_date: snapshotDateParam,
        p_limit: 10000,
        p_offset: 0
      });

      data = rpcData || [];
      error = rpcError;

      if (rpcError) {
        console.error(
          '[spatialClustering] get_recalls_for_viewport RPC error (no tenant):',
          rpcError
        );
        if (rpcError.message?.includes('function') || rpcError.code === '42883') {
          console.warn(
            '[spatialClustering] get_recalls_for_viewport RPC function not found - migration may not be applied'
          );
        }
      } else {
        console.log(
          `[spatialClustering] get_recalls_for_viewport (no tenant) returned ${data.length} recalls for snapshot ${snapshotDateParam || 'all dates'}`
        );
      }
    }

    if (error) {
      console.error('Error fetching recall clusters:', error);

      // If it's a permission error, return empty array
      if (error.message?.includes('403') || error.message?.includes('permission denied')) {
        console.warn('Permission denied, returning empty clusters');
        return [];
      }

      // For other errors, also return empty
      console.warn('Query error, returning empty clusters');
      return [];
    }

    // Filter out excluded dealers client-side
    let filteredData = data || [];
    if (excludedDealers.length > 0) {
      const excludedSet = new Set(excludedDealers);
      filteredData = filteredData.filter((r: any) => !excludedSet.has(r.service_provider_name));
    }

    // Filter by OEM: recall counts are always for the selected OEM only (Ford, Stellantis, etc.)
    const oem = options?.oem ?? DEFAULT_OEM;
    const oemBrands = getBrandsForOEM(oem);
    const oemBrandsLower =
      oemBrands.length > 0 ? oemBrands.map((b: string) => b.toLowerCase()) : [];
    const dataForClusters =
      oemBrandsLower.length > 0
        ? filteredData.filter((r: any) =>
            oemBrandsLower.includes((r.make_name || '').toLowerCase())
          )
        : [];

    // Group by dealer location to create clusters
    // Track unique (vin, campaign_number) pairs per dealer for accurate recall counts
    const dealerMap = new Map<
      string,
      {
        name: string;
        lat: number;
        lon: number;
        address: string;
        city: string;
        state: string;
        zip: string;
        vins: Set<string>;
        recallPairs: Set<string>; // Track unique (vin, campaign_number) pairs
        makes: Set<string>;
      }
    >();

    dataForClusters.forEach(row => {
      try {
        // Handle potentially corrupted JSON data in safety_recalls
        let safetyRecalls = row.safety_recalls;

        // If safety_recalls is a string, try to parse it
        if (typeof safetyRecalls === 'string') {
          try {
            safetyRecalls = JSON.parse(safetyRecalls);
          } catch (parseError) {
            console.warn(
              `Skipping row with malformed safety_recalls JSON for VIN: ${row.vin}`,
              parseError
            );
            return; // Skip this row
          }
        }

        // Apply M/N campaign filtering — Ford OEM only.
        // GM and other OEMs use 'N' prefix for legitimate NHTSA recalls.
        if (safetyRecalls && Array.isArray(safetyRecalls)) {
          const validRecalls = filterSafetyRecallsForMapDisplayByOem(safetyRecalls, oem === 'Ford');

          // Skip this row if no valid recalls after filtering
          if (validRecalls.length === 0) {
            return;
          }

          const dealerKey = `${row.service_provider_name}_${row.seller_latitude}_${row.seller_longitude}`;
          const vin = row.vin || '';
          if (!vin) return;

          if (!dealerMap.has(dealerKey)) {
            dealerMap.set(dealerKey, {
              name: row.service_provider_name || 'Unknown',
              lat: row.seller_latitude,
              lon: row.seller_longitude,
              address: row.seller_street || 'N/A',
              city: row.seller_city || 'N/A',
              state: row.seller_region || 'N/A',
              zip: row.seller_postal_code || 'N/A',
              vins: new Set(),
              recallPairs: new Set(), // Track unique (vin, campaign_number) pairs
              makes: new Set<string>() // Track unique vehicle makes for OEM filtering
            });
          }

          const dealer = dealerMap.get(dealerKey)!;
          dealer.vins.add(vin);

          // Track vehicle make for OEM filtering
          if (row.make_name) {
            dealer.makes.add(row.make_name);
          }

          // Add unique (vin, campaign_number) pairs for this dealer
          validRecalls.forEach((recall: any) => {
            const campaignNumber = recall?.campaign_number || '';
            if (campaignNumber) {
              const recallKey = `${vin}_${campaignNumber}`;
              dealer.recallPairs.add(recallKey);
            }
          });
        }
      } catch (error) {
        console.warn(`Error processing row for VIN: ${row.vin}`, error);
        // Continue processing other rows
      }
    });

    // Convert to RecallCluster format
    // Use size of recallPairs Set to get accurate count of unique (vin, campaign_number) pairs
    const clusters = Array.from(dealerMap.entries()).map(([key, dealer]) => ({
      clusterId: key,
      centerLat: dealer.lat,
      centerLon: dealer.lon,
      recallCount: dealer.recallPairs.size, // Count of unique (vin, campaign_number) pairs
      vehicleCount: dealer.vins.size,
      dealerCount: 1,
      dealerNames: [dealer.name],
      makes: Array.from(dealer.makes), // Unique vehicle makes for OEM filtering
      sampleDealers: [
        {
          name: dealer.name,
          lat: dealer.lat,
          lon: dealer.lon,
          address: dealer.address,
          city: dealer.city,
          state: dealer.state,
          zip: dealer.zip
        }
      ]
    }));

    // Debug: Log total recall count
    const totalRecalls = clusters.reduce((sum, cluster) => sum + cluster.recallCount, 0);
    console.log(
      `[spatialClustering] Total unique (vin, campaign_number) pairs: ${totalRecalls} across ${clusters.length} dealers`
    );

    return clusters;
  } catch (error) {
    console.error('Error in getRecallClusters:', error);
    throw error;
  }
}

/**
 * Get dealer-level clusters for map viewport
 * Alternative approach that aggregates by dealer location
 * Better for moderate zoom levels where individual dealers are meaningful
 */
export async function getDealerClusters(
  bounds: ViewportBounds,
  userId?: string,
  options?: GetRecallClustersOptions
): Promise<DealerCluster[]> {
  try {
    // Use prod schema client to access prod.cars_with_safety_recalls_mv
    const prodClient = getProdSupabaseClient();

    // Apply dealer exclusions if userId provided
    const excludedDealers = userId
      ? await import('@/features/recalls/services/queryBuilder').then(({ getExcludedDealers }) =>
          getExcludedDealers(prodClient, userId)
        )
      : [];

    // Use RPC function to get tenant-scoped recalls with viewport bounds
    // For Control Center (super_admin), we need to show all recalls regardless of tenant zones
    const tenantId = await getTenantIdFromSession(prodClient);

    // Check if tenant is internal (skip zip code filtering)
    let isInternalTenant = false;
    if (tenantId) {
      try {
        const { data: tenantData } = await prodClient
          .from('tenants')
          .select('tenant_type')
          .eq('id', tenantId)
          .maybeSingle();

        isInternalTenant = tenantData?.tenant_type === 'internal';
      } catch (err) {
        console.warn('[spatialClustering] Failed to check tenant type for dealer clusters:', err);
      }
    }

    // Check if user is super_admin by querying their role
    let isSuperAdmin = false;
    if (userId) {
      try {
        // Query user_roles with join to roles table to check for super_admin
        const { data: roleData } = await prodClient
          .from('user_roles')
          .select('role_id, roles(id, name)')
          .eq('user_id', userId)
          .eq('is_active', true)
          .limit(10); // Get all active roles to check for super_admin

        // Check if any role has name 'super_admin'
        isSuperAdmin = (roleData || []).some((ur: any) => ur.roles?.name === 'super_admin');
      } catch (err) {
        console.warn(
          '[spatialClustering] Failed to check super_admin role for dealer clusters:',
          err
        );
      }
    }

    let data: any[] = [];
    let error: any = null;

    if (tenantId) {
      // Try tenant-scoped query first
      let result;
      try {
        result = await getRecallsForTenantWithFilters(prodClient, tenantId, {
          southLat: bounds.south,
          northLat: bounds.north,
          westLng: bounds.west,
          eastLng: bounds.east,
          limit: 10000,
          offset: 0
        });

        data = result.data || [];
        error = result.error;
      } catch (err: any) {
        // If the function doesn't exist or fails, treat as error and fall back
        console.warn(
          '[spatialClustering] getRecallsForTenantWithFilters failed for dealer clusters:',
          err
        );
        error = err;
        data = [];
      }

      // Only fall back to querying all recalls if:
      // 1. User is super_admin (should see all recalls)
      // 2. OR tenant-scoped query returns an actual error (not just empty data)
      // For regular tenants with no zones, return empty data (don't show all recalls)
      if (isSuperAdmin && (error || !data || data.length === 0)) {
        console.log(
          '[spatialClustering] Super admin detected for dealer clusters - falling back to all recalls'
        );

        // Use RPC function that bypasses tenant zone filtering
        // Convert snapshot date string to Date object if provided
        const snapshotDateParam = options?.snapshotDate
          ? options.snapshotDate.includes('T')
            ? options.snapshotDate.split('T')[0]
            : options.snapshotDate
          : null;

        // Fetch all data using pagination if needed
        // Tier 1 Fix: Use stable abort ID to detect viewport/snapshot changes during fetch
        const abortId = `${snapshotDateParam || 'all'}:${bounds.south}:${bounds.north}:${bounds.west}:${bounds.east}`;
        let allRpcData: any[] = [];
        const PAGE_SIZE = 10000;
        let offset = 0;
        let hasMore = true;
        let pageCount = 0;
        const MAX_PAGES = 50; // Safety limit: 500k rows max

        let rpcError: any = null;
        while (hasMore && pageCount < MAX_PAGES) {
          const result = await prodClient.rpc('get_recalls_for_viewport', {
            p_south_lat: bounds.south,
            p_north_lat: bounds.north,
            p_west_lng: bounds.west,
            p_east_lng: bounds.east,
            p_snapshot_date: snapshotDateParam,
            p_limit: PAGE_SIZE,
            p_offset: offset
          });

          rpcError = result.error;
          if (rpcError) {
            error = rpcError;
            hasMore = false;
            if (rpcError.message?.includes('function') || rpcError.code === '42883') {
              console.warn(
                '[spatialClustering] get_recalls_for_viewport RPC function not found - migration may not be applied'
              );
            }
            break;
          }

          const pageData = result.data || [];
          allRpcData = [...allRpcData, ...pageData];

          // Check if we got a full page (means there might be more)
          if (pageData.length < PAGE_SIZE) {
            hasMore = false;
          } else {
            offset += PAGE_SIZE;
            pageCount++;
          }
        }

        // Tier 1 Fix: De-duplicate by unique (vin, campaign_number) pairs to avoid double-counting across pages
        // Track unique pairs in a Set, then rebuild rows grouped by VIN with deduplicated safety_recalls arrays
        const uniquePairs = new Set<string>();
        const vinRowMap = new Map<string, any>();

        for (const row of allRpcData) {
          const vin = row.vin;
          if (!vin) continue;

          // Initialize VIN entry if not seen
          if (!vinRowMap.has(vin)) {
            vinRowMap.set(vin, {
              ...row,
              safety_recalls: []
            });
          }

          const vinRow = vinRowMap.get(vin);
          const safetyRecalls = row.safety_recalls;

          // Expand safety_recalls JSONB array and deduplicate by vin::campaign_number
          if (safetyRecalls && Array.isArray(safetyRecalls)) {
            for (const recall of safetyRecalls) {
              const campaignNumber = recall?.campaign_number;
              if (!campaignNumber) continue;

              const pairKey = `${vin}::${campaignNumber}`;

              // Only add this (vin, campaign_number) pair if we haven't seen it before
              if (!uniquePairs.has(pairKey)) {
                uniquePairs.add(pairKey);
                vinRow.safety_recalls.push(recall);
              }
            }
          }
        }

        // Convert map back to array and filter out rows with no recalls
        const dedupedData = Array.from(vinRowMap.values()).filter(
          row => row.safety_recalls && row.safety_recalls.length > 0
        );

        const totalRowsBefore = allRpcData.length;
        const totalRowsAfter = dedupedData.length;
        const totalUniquePairs = uniquePairs.size;

        if (totalRowsBefore !== totalRowsAfter) {
          console.warn(
            `[spatialClustering] Deduplicated ${totalRowsBefore} rows to ${totalRowsAfter} unique VIN rows with ${totalUniquePairs} unique (vin, campaign_number) pairs for dealer clusters`
          );
        }

        if (!error && dedupedData.length > 0) {
          // For all tenants (internal and dealer), filter to latest snapshot_date per zone_slug
          // This prevents double counting of the same recalls across multiple snapshot dates
          const { filterRecallsByLatestSnapshotPerZoneSlug } = await import(
            '@/features/admin/utils/filterRecallsByLatestSnapshot'
          );
          const filteredData = await filterRecallsByLatestSnapshotPerZoneSlug(dedupedData);
          data = filteredData;
          error = null;
          if (pageCount > 0) {
            console.log(
              `[spatialClustering] Fallback query for dealer clusters returned ${allRpcData.length} total rows (${dedupedData.length} unique VIN rows with ${totalUniquePairs} unique pairs after dedupe, ${filteredData.length} after latest snapshot filter) across ${pageCount + 1} page(s)`
            );
          } else {
            console.log(
              `[spatialClustering] Fallback query for dealer clusters returned ${dedupedData.length} unique VIN rows with ${totalUniquePairs} unique (vin, campaign_number) pairs (${filteredData.length} after latest snapshot filter)`
            );
          }
        }
      }
    } else {
      // No tenant ID - query all recalls (for super_admin or Control Center)
      console.log(
        '[spatialClustering] No tenant ID for dealer clusters, querying all recalls using get_recalls_for_viewport'
      );

      // Use RPC function that bypasses tenant zone filtering
      // Convert snapshot date string to Date object if provided
      const snapshotDateParam = options?.snapshotDate
        ? options.snapshotDate.includes('T')
          ? options.snapshotDate.split('T')[0]
          : options.snapshotDate
        : null;

      const { data: rpcData, error: rpcError } = await prodClient.rpc('get_recalls_for_viewport', {
        p_south_lat: bounds.south,
        p_north_lat: bounds.north,
        p_west_lng: bounds.west,
        p_east_lng: bounds.east,
        p_snapshot_date: snapshotDateParam,
        p_limit: 10000,
        p_offset: 0
      });

      data = rpcData || [];
      error = rpcError;

      if (rpcError) {
        console.error(
          '[spatialClustering] get_recalls_for_viewport RPC error for dealer clusters (no tenant):',
          rpcError
        );
        if (rpcError.message?.includes('function') || rpcError.code === '42883') {
          console.warn(
            '[spatialClustering] get_recalls_for_viewport RPC function not found - migration may not be applied'
          );
        }
      } else {
        console.log(
          `[spatialClustering] get_recalls_for_viewport for dealer clusters (no tenant) returned ${data.length} recalls`
        );
      }
    }

    if (error) {
      console.error('Error fetching dealer clusters:', error);
      throw new Error(`Failed to fetch dealer clusters: ${error.message}`);
    }

    // Filter out excluded dealers client-side
    if (excludedDealers.length > 0) {
      const excludedSet = new Set(excludedDealers);
      data = data.filter((r: any) => !excludedSet.has(r.service_provider_name));
    }

    // Group by dealer location to create clusters
    const dealerMap = new Map<
      string,
      {
        name: string;
        lat: number;
        lon: number;
        address: string;
        city: string;
        state: string;
        zip: string;
        vins: Set<string>;
        campaigns: Set<string>;
        recallCount: number;
      }
    >();

    (data || []).forEach(row => {
      try {
        // Handle potentially corrupted JSON data in safety_recalls
        let safetyRecalls = row.safety_recalls;

        // If safety_recalls is a string, try to parse it
        if (typeof safetyRecalls === 'string') {
          try {
            safetyRecalls = JSON.parse(safetyRecalls);
          } catch (parseError) {
            console.warn(
              `Skipping row with malformed safety_recalls JSON for VIN: ${row.vin}`,
              parseError
            );
            return; // Skip this row
          }
        }

        // Apply M/N campaign filtering — Ford OEM only.
        // GM and other OEMs use 'N' prefix for legitimate NHTSA recalls.
        if (safetyRecalls && Array.isArray(safetyRecalls)) {
          const validRecalls = filterSafetyRecallsForMapDisplayByOem(
            safetyRecalls,
            (options?.oem ?? DEFAULT_OEM) === 'Ford'
          );

          // Skip this row if no valid recalls after filtering
          if (validRecalls.length === 0) {
            return;
          }

          const dealerKey = `${row.service_provider_name}_${row.seller_latitude}_${row.seller_longitude}`;

          if (!dealerMap.has(dealerKey)) {
            dealerMap.set(dealerKey, {
              name: row.service_provider_name || 'Unknown',
              lat: row.seller_latitude,
              lon: row.seller_longitude,
              address: row.seller_street || 'N/A',
              city: row.seller_city || 'N/A',
              state: row.seller_region || 'N/A',
              zip: row.seller_postal_code || 'N/A',
              vins: new Set(),
              campaigns: new Set(),
              recallCount: 0
            });
          }

          const dealer = dealerMap.get(dealerKey)!;
          dealer.vins.add(row.vin);
          dealer.recallCount += validRecalls.length;

          // Extract campaign numbers from valid recalls only
          validRecalls.forEach((recall: any) => {
            if (recall.campaign_number) {
              dealer.campaigns.add(recall.campaign_number);
            }
          });
        }
      } catch (error) {
        console.warn(`Error processing row for VIN: ${row.vin}`, error);
        // Continue processing other rows
      }
    });

    // Convert to DealerCluster format
    return Array.from(dealerMap.entries()).map(([key, dealer]) => ({
      dealerKey: key,
      dealerName: dealer.name,
      dealerLat: dealer.lat,
      dealerLon: dealer.lon,
      recallCount: dealer.recallCount,
      vehicleCount: dealer.vins.size,
      campaignCount: dealer.campaigns.size,
      dealerInfo: {
        address: dealer.address,
        city: dealer.city,
        state: dealer.state,
        zip: dealer.zip,
        phone: 'N/A'
      }
    }));
  } catch (error) {
    console.error('Error in getDealerClusters:', error);
    throw error;
  }
}

/**
 * Determine optimal zoom level from map bounds
 * Helper function to calculate appropriate zoom level for spatial binning
 */
export function calculateZoomLevel(bounds: ViewportBounds): number {
  const latRange = bounds.north - bounds.south;
  const lonRange = bounds.east - bounds.west;
  const area = latRange * lonRange;

  // Map viewport area to zoom level (approximate)
  if (area > 100) return 4; // Continental view
  if (area > 50) return 6; // Multi-state
  if (area > 20) return 8; // Regional
  if (area > 10) return 10; // State-level
  if (area > 5) return 12; // Metro area
  if (area > 1) return 14; // City-level
  return 16; // Neighborhood
}

/**
 * Determine whether to use hex clustering or dealer clustering
 * Based on zoom level and expected data density
 */
export function shouldUseHexClustering(bounds: ViewportBounds, zoomLevel?: number): boolean {
  const zoom = zoomLevel || calculateZoomLevel(bounds);

  // Use hex clustering for wide zoom levels where individual dealers would be too dense
  // Use dealer clustering for closer zoom levels where dealers are meaningful
  return zoom <= 10;
}

/**
 * Get optimal clusters for viewport (automatically chooses best method)
 * Returns unified interface regardless of clustering method used
 */
export async function getOptimalClusters(
  bounds: ViewportBounds,
  userId?: string,
  options?: GetRecallClustersOptions
): Promise<{
  clusters: (RecallCluster | DealerCluster)[];
  clusterType: 'hex' | 'dealer';
  zoomLevel: number;
}> {
  const zoomLevel = calculateZoomLevel(bounds);
  const useHexClustering = shouldUseHexClustering(bounds, zoomLevel);

  console.log(
    `🎯 Spatial clustering: zoom=${zoomLevel}, method=${useHexClustering ? 'hex' : 'dealer'}`
  );

  // For now, always use get_recall_clusters since it's working
  // The function internally does dealer-level aggregation anyway
  const clusters = await getRecallClusters(bounds, zoomLevel, userId, options);

  return {
    clusters,
    clusterType: 'dealer', // Always dealer-level since that's what our function does
    zoomLevel
  };
}
